<template>
  <div class="pr-0.5 relative">
    <div
      class="relative flex w-full justify-center rounded-sm py-1 text-center text-xs font-semibold transition-colors"
      :class="[
        isCurrentOrPrior ? 'active' : 'inactive',
        disablePhaseChange ? 'cursor-not-allowed' : 'cursor-pointer',
        { 'highlight-white': (isCurrentOrPrior && isActive) || !isBuilderView },
        { highlight: !isCurrentOrPrior && isActive },
      ]"
      :data-cy="`phase-tag-${phaseName}`"
      :style="{}"
      @mouseenter="showIconsWithTooltip = true"
      @mouseleave="showIconsWithTooltip = false"
    >
      <div
        v-if="!first"
        class="arrow-negative absolute left-0 z-10 mt-[20px] h-0 w-0"
      />
      <div class="flex items-center whitespace-nowrap uppercase gap-x-2">
        <icon-base
          v-if="showDeadIcon"
          height="12"
          width="12"
          :icon="IconFail"
          data-cy="icon-dead"
        />
        <icon-base
          v-else-if="showCompletedIcon"
          class="text-checkmark-green"
          height="14"
          width="12"
          :icon="IconSuccessRound"
          data-cy="icon-complete"
        />
        {{ formattedPhaseName(phaseName) }}
      </div>
      <div class="absolute right-4" :class="[dynamicIconsStyles]">
        <div
          v-if="isManagedByLendflow || isManagedByClient"
          v-tooltip="tooltipText"
        >
          <icon-base
            v-if="isManagedByLendflow"
            height="12"
            width="12"
            :icon="lendflowIconStyle"
            viewBox="0 0 20 30"
          />
          <template v-if="isManagedByClient">
            <icon-base
              v-if="isManagedByLendflow && isManagedByClient"
              height="14"
              width="14"
              :icon="plusIconType"
              viewBox="0 0 60 60"
            />
            <icon-base
              height="12"
              width="12"
              :icon="organizationIconType"
              viewBox="0 1 16 16"
            />
          </template>
        </div>
      </div>
      <div
        class="absolute right-1"
        :class="{
          'text-white': isActive && isCurrentOrPrior,
        }"
      >
        <icon-base v-if="last" :icon="IconFlag" />
      </div>
      <div
        v-if="!last"
        class="arrow absolute right-0 z-20 mt-[20px] mr-[2.3px] h-0 w-0"
        :class="[isCurrentOrPrior ? 'arrow-active' : 'arrow-negative']"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from "vue";
import { useI18n } from "vue-i18n";

import IconFail from "@/components/icons/IconFail.vue";
import IconSuccessRound from "@/components/icons/IconSuccessRound.vue";
import { useScorecardsBase } from "@/hooks/scorecards";

const {
  phaseName = "",
  currentPhase = 0,
  activePhase = 0,
  first = false,
  last = false,
  index = 0,
  showCompletedIcon = false,
  showDeadIcon = false,
  disablePhaseChange = false,
  isManagedByLendflow = false,
  isManagedByClient = false,
  clientName = "",
} = defineProps<{
  phaseName?: string;
  currentPhase?: number; // The current phase of the deal
  activePhase?: number; // The phase selected by the user
  first?: boolean;
  last?: boolean;
  index?: string | number;
  showCompletedIcon?: boolean;
  showDeadIcon?: boolean;
  disablePhaseChange?: boolean;
  isManagedByLendflow?: boolean;
  isManagedByClient?: boolean;
  clientName?: string;
}>();

const IconFlag = defineAsyncComponent(
  () => import("@/components/icons/IconFlag.vue")
);

const { t } = useI18n();
const { isBuilderView } = useScorecardsBase();

const showIconsWithTooltip = ref(false);

const isCurrentOrPrior = computed(() => Number(index) <= currentPhase);

const isActive = computed(() => Number(index) === activePhase);

const dynamicIconsStyles = computed(() => {
  if (showIconsWithTooltip.value && isCurrentOrPrior.value) {
    return "visible icons__background--blue pl-5 pr-3";
  } else if (showIconsWithTooltip.value && !isCurrentOrPrior.value) {
    return "visible icons__background--grey pl-5 pr-3";
  } else {
    return "invisible";
  }
});

const organizationIconType = computed(() =>
  isCurrentOrPrior.value ? "organization-white" : "organization"
);

const lendflowIconStyle = computed(() =>
  isCurrentOrPrior.value ? "logo-white" : "logo-blue"
);

const plusIconType = computed(() =>
  isCurrentOrPrior.value ? "plus-no-color" : "plus-blue"
);

const tooltipText = computed(() => {
  if (isManagedByLendflow && isManagedByClient) {
    return t("DEALS.CLIENT_PHASES.ACCOUNTABLE", {
      name: t("COMMON.BOTH"),
    });
  } else if (isManagedByLendflow) {
    return t("DEALS.CLIENT_PHASES.ACCOUNTABLE", {
      name: t("COMMON.LENDFLOW"),
    });
  } else if (isManagedByClient) {
    return t("DEALS.CLIENT_PHASES.ACCOUNTABLE", {
      name: clientName,
    });
  } else {
    return "";
  }
});

const formattedPhaseName = (phaseName: string) =>
  phaseName === t("DEALS.CLIENT_PHASES.OFFER_OUT")
    ? t("COMMON.OFFER")
    : phaseName;
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.active {
  @apply bg-primary text-white/50;
}
.active:hover {
  @apply text-white/100;
}
.inactive {
  @apply bg-medium-grey text-disabled-medium;
}

.highlight {
  @apply text-primary;
}

.highlight-white {
  @apply text-white!;
}

.active:hover {
  @apply bg-primary-link-hover;
}
.arrow:after,
.arrow-active:after,
.arrow-negative:after {
  content: "";
  display: block;
  position: absolute;
  bottom: 100%;
  border-bottom: 12px solid transparent;
  border-top: 13px solid transparent;
  border-right: 7px solid transparent;
}

.arrow-negative:after {
  border-left: 11px solid white;
}

.arrow:after {
  border-left: 11px solid #f3f4f6;
}
.arrow-active:after {
  border-left: 11px solid #3769d6;
}
.active:hover .arrow-active:after {
  border-left: 11px solid #325ec1;
}

.icons__background--blue {
  background: linear-gradient(
    270deg,
    #3769d6 78.09%,
    rgba(255, 255, 255, 0) 100%
  );
}

.active:hover .icons__background--blue,
.highlight:hover .icons__background--blue {
  background: linear-gradient(
    270deg,
    #325ec1 78.09%,
    rgba(255, 255, 255, 0) 100%
  );
}

.icons__background--grey {
  background: linear-gradient(
    270deg,
    #f3f4f6 78.09%,
    rgba(255, 255, 255, 0) 100%
  );
}
</style>
